/**
 * Defined Account Holds and Indicators (CCF-79). Static reference list of the
 * Unibanks hold codes the agent can cross-reference against a hold's "Hold"
 * column. Updated on a daily basis in the real system; mirrored here so the
 * agent never has to look the meaning up by eye (issue #108).
 *
 * Covers BOTH the single-letter codes (A–Z) AND the numeric codes (0–9). The
 * gateway can return either against an account (issue #224: customer 4270190968
 * carried numeric holds 3 and 4 that rendered as bare codes with no
 * explanation, because the catalogue was letter-only). The numeric codes and
 * their descriptions are sourced from the authoritative
 * `/bankcontrol/accountholds` static holds table mirrored in the gateway
 * contract (`app/contracts/gateway/accounts.r4.yaml` — the daily-updated
 * `HoldValues_List`); the fixed-width gateway descriptions are expanded to
 * their natural full form.
 *
 * Shared by the full Account Holds screen (SCRN-42 / CCF-79) and the customer
 * dashboard's inline "View Holds" expansion (CCF-83), so BOTH surfaces render
 * the code together with its explanation — e.g. "M — Only Deposit Services can
 * amend" or "3 — No Stmts Issued - Check customer address" rather than the bare
 * code (issues #108 + #224).
 */
export const DEFINED_HOLDS: readonly [string, string][] = [
  // Numeric hold codes (0–9), from the /bankcontrol/accountholds static table
  // (accounts.r4.yaml HoldValues_List) — issue #224.
  ['0', 'Inactive A/C. Valid AML documentation required'],
  ['1', 'Suspense A/c System Transactions'],
  ['2', 'Suspense Account'],
  ['3', 'No Stmts Issued - Check customer address'],
  ['4', 'Charged off accounts. Lodgements only'],
  ['5', 'Trans Denied - For IT Use'],
  ['6', 'Irish Life Account - CANCELLED'],
  ['7', 'DGS Exempt Account'],
  ['8', 'Hold on A/c. Contact Open24'],
  ['9', 'No Deposits Allowed'],
  ['A', 'AML Exit - only DS users'],
  ['B', 'Update customer info today'],
  ['C', 'Legal Proceedings'],
  ['D', 'Is Description Keyed?'],
  ['E', 'Deceased Owner(s)'],
  ['F', 'Open24 originated application'],
  ['G', 'CAUTION! refer to comments'],
  ['H', 'Awaiting AML Documentation'],
  ['I', 'Awaiting Customer ID'],
  ['J', 'Multiple Warnings'],
  ['K', 'Cannot create SEPA DD'],
  ['L', 'Capture Customer Signature'],
  ['M', 'Only Deposit Services can amend'],
  ['N', 'Pay No Withdrawal'],
  ['O', 'CRs Status Hold'],
  ['P', 'Overdraft Setoff'],
  ['Q', 'Refer to FCPU'],
  ['R', 'Cannot create DD - R Hold'],
  ['S', 'A/c Suspended/Written Off'],
  ['T', 'Arrears/Overdrawn A/Cs'],
  ['U', 'Refer to Account Mandate'],
  ['V', 'Victim hold'],
  ['W', 'For Area Office Use Only'],
  ['X', 'Minor Acct'],
  ['Y', 'Unknown Customer (use T)'],
  ['Z', 'Account in dispute'],
];

/**
 * Code → explanation lookup derived from {@link DEFINED_HOLDS}, so a hold can
 * show its human-readable meaning next to its code (letter OR numeric) without
 * the agent having to cross-reference the "Defined Account Holds and
 * Indicators" list by eye (issues #108 + #224).
 */
const HOLD_CODE_EXPLANATION: Record<string, string> = Object.fromEntries(
  DEFINED_HOLDS.map(([code, text]) => [code.toUpperCase(), text]),
);

/**
 * The catalogue explanation for a single hold code, or `undefined` when the
 * code is blank/unknown. Case-insensitive; accepts letter and numeric codes.
 */
export function holdCodeExplanation(
  code: string | undefined,
): string | undefined {
  const c = (code ?? '').trim();
  if (!c) return undefined;
  return HOLD_CODE_EXPLANATION[c.toUpperCase()];
}

/**
 * Render a hold code followed by its catalogue explanation when the code is one
 * of the Defined Account Holds and Indicators (issue #108) — e.g.
 * "M — Only Deposit Services can amend". Unknown/blank codes fall back to just
 * the raw code.
 */
export function holdCodeLabel(code: string | undefined): string {
  const c = (code ?? '').trim();
  if (!c) return '';
  const explanation = holdCodeExplanation(c);
  return explanation ? `${c} — ${explanation}` : c;
}

/**
 * Render a hold as `<code> — <comment>` where `<comment>` is the per-hold
 * free-text the gateway returns for THAT specific hold (the `account/holds/
 * details` op's `HoldDescriptionN` field, surfaced as the hold's `comment`) —
 * e.g. "N — FREEZE ORDER IN PLACE" rather than the generic catalogue
 * description "N — Pay No Withdrawal" (issue #465, @dylan219 2026-07-22).
 *
 * This ADJUSTS the #108 `<code> — <catalogue-description>` convention on the
 * hold-warning surfaces: the per-hold comment is what the agent needs, and the
 * catalogue meaning is still available in the "Defined Account Holds and
 * Indicators" reference table (SCRN-42) — see #108/#426 for the decision trail.
 *
 * When the gateway returns no comment for the hold, we fall back to the
 * catalogue explanation via {@link holdCodeLabel} so the agent never sees a
 * bare, unexplained code. Blank codes render as ''.
 *
 * NOTE (#524): this catalogue fallback is ONLY for the surfaces that render a
 * hold WITHOUT a dedicated comment slot (the account-overview drill-down
 * "Account holds" field and the dashboard tile's code-only projection before
 * the detailed holds fetch lands). The dedicated COMMENT slots — the SCRN-42
 * Comments column and the dashboard tile's expanded hold line once
 * `account/holds/details` has answered — must render the comment verbatim and
 * stay BLANK when the gateway carries none; use {@link holdCommentLabel} there.
 */
export function holdLabel(
  code: string | undefined,
  comment: string | undefined,
): string {
  const c = (code ?? '').trim();
  if (!c) return '';
  const text = (comment ?? '').trim();
  return text ? `${c} — ${text}` : holdCodeLabel(c);
}

/**
 * Render a hold as `<code> — <comment>` with NO catalogue fallback (issue
 * #524): the comment is the per-hold free-text the gateway returns for THAT
 * specific hold (`account/holds/details` `HoldDescriptionN`), rendered
 * verbatim. When the gateway carries no comment for the hold
 * (`HoldDescriptionN` empty/whitespace — verified live on staging), only the
 * bare code renders — the catalogue description must NOT be substituted into a
 * comment slot, because the host's "Further description" is genuinely blank
 * and inventing one misleads the agent. Blank codes render as ''.
 */
export function holdCommentLabel(
  code: string | undefined,
  comment: string | undefined,
): string {
  const c = (code ?? '').trim();
  if (!c) return '';
  const text = (comment ?? '').trim();
  return text ? `${c} — ${text}` : c;
}
